import { useEffect, useRef, useState } from 'react';
import { findPlaces } from '@/screens/parts/HouseLocationPicker';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Check, Download, Map as MapIcon, Pencil, Satellite, Search, Trash2, Undo2, Upload } from 'lucide-react';
import { readBoundaryFile } from '@/lib/boundaryImport';
import { SQM_PER_HA, formatArea, geometryAreaSqm } from '@/lib/area';

/**
 * Plot boundary map: draw a polygon, edit its vertices, download it as GeoJSON.
 *
 * Leaflet directly, with no drawing plugin. leaflet-draw is unmaintained and its React
 * wrappers fight React 19's lifecycle; the interaction needed here is small enough to own:
 * click to add a vertex, drag a vertex to move it, undo the last one, finish. Owning it also
 * means the map works the same on a field tablet as on a desktop.
 *
 * Tiles come from OpenStreetMap - no key, no account, and a licence that permits this use.
 * Attribution is required and is rendered by Leaflet itself; do not remove it.
 */

export interface PolygonGeoJson {
  type: 'Polygon';
  coordinates: number[][][];
}

export interface MultiPolygonGeoJson {
  type: 'MultiPolygon';
  coordinates: number[][][][];
}

/**
 * The column is MultiPolygon, so that is what comes back even for a plot drawn as one ring.
 * Drawing always produces a Polygon and the server promotes it; both have to render.
 */
export type PlotGeometry = PolygonGeoJson | MultiPolygonGeoJson;

export const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
export const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

/**
 * Satellite imagery (owner, 2026-09-21: "add satellite view"). Esri World Imagery needs no key
 * and permits this use with attribution; a boundary is walked against what is actually on the
 * ground - field edges, tree lines, streams - which a street map does not show. Google imagery
 * would need a Google Maps Platform key and its own SDK: its terms do not allow its tiles in
 * Leaflet directly. Place names are laid over the imagery so an officer can still find the
 * village.
 */
export const SATELLITE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
export const SATELLITE_ATTRIBUTION = 'Imagery &copy; Esri, Maxar, Earthstar Geographics, and the GIS User Community';
export const LABELS_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}';

/** Bright yellow reads on both satellite greens and the street map; brand green vanished on imagery. */
const BOUNDARY = '#facc15';

/** One colour per asset group, so a map of a plot reads at a glance. */
export const ASSET_GROUP_COLOURS: Record<string, string> = {
  Infrastructure: '#60a5fa',
  'Farm site': '#fb923c',
  Water: '#22d3ee',
  'HCV & cultural': '#c084fc',
  Safety: '#f87171',
};

export interface AssetType { id: string; label: string; geometry_type: 'point' | 'line' | 'area'; asset_group: string }
export interface AssetShape { id: string; label: string; group: string | null; geometry: { type: string; coordinates: unknown } | null }

/** India, so an empty map is not the middle of the Atlantic. */
const DEFAULT_CENTRE: [number, number] = [22.9734, 78.6569];

export function PlotMap({
  value,
  onChange,
  readOnly,
  plotCode,
  assets = [],
  assetTypes = [],
  onMarkAsset,
  placeHint,
}: {
  value: PlotGeometry | null;
  onChange?: (geometry: PolygonGeoJson | null) => void;
  readOnly?: boolean;
  plotCode?: string;
  /** Assets and risks already marked on this plot (risk assessment, owner 2026-09-21). */
  assets?: AssetShape[];
  assetTypes?: AssetType[];
  /** A new asset was marked; the parent asks for its details and saves it. */
  onMarkAsset?: (type: AssetType, geometry: { type: string; coordinates: unknown }) => void;
  /** "Village, Block, District, State": with no boundary yet, the map opens there, not on India. */
  placeHint?: string | null;
}) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [points, setPoints] = useState<Array<[number, number]>>([]);
  // Satellite by default: a boundary is traced against the ground, not against a road map.
  const [base, setBase] = useState<'satellite' | 'map'>('satellite');
  const baseLayers = useRef<{ satellite: L.Layer; map: L.Layer } | null>(null);
  const assetLayer = useRef<L.LayerGroup | null>(null);
  // Marking an asset: the chosen type, and the points of a line or area in progress.
  const [marking, setMarking] = useState<AssetType | null>(null);
  const [markPoints, setMarkPoints] = useState<Array<[number, number]>>([]);

  // Leaflet mutates the DOM itself, so it is created once and then driven by effects. A
  // dependency on `points` here would tear the map down on every click.
  useEffect(() => {
    if (!container.current || map.current) return;
    const instance = L.map(container.current, { center: DEFAULT_CENTRE, zoom: 5, zoomControl: true });
    const street = L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 19 });
    // Imagery is only captured to about z18 in rural India; beyond that Leaflet stretches the
    // last tile rather than showing grey, so a small plot can still be drawn close up.
    const satellite = L.layerGroup([
      L.tileLayer(SATELLITE_URL, { attribution: SATELLITE_ATTRIBUTION, maxNativeZoom: 18, maxZoom: 20 }),
      L.tileLayer(LABELS_URL, { maxNativeZoom: 18, maxZoom: 20, opacity: 0.9 }),
    ]);
    satellite.addTo(instance);
    baseLayers.current = { satellite, map: street };
    layer.current = L.layerGroup().addTo(instance);
    assetLayer.current = L.layerGroup().addTo(instance);
    map.current = instance;
    // The container is often sized by a drawer that animates open; without this the tiles
    // render into a zero-height box and the map looks broken until the window is resized.
    setTimeout(() => instance.invalidateSize(), 150);
    return () => { instance.remove(); map.current = null; layer.current = null; assetLayer.current = null; baseLayers.current = null; };
  }, []);

  // Place search (owner, 2026-09-21: "add search option as in the farm GPS map, prefilled from the
  // village, block, district, state"). Starts with the plot's own place; any village, landmark or
  // pasted coordinates work. It only moves the map - nothing is saved from it.
  const [query, setQuery] = useState(placeHint ?? '');
  const [places, setPlaces] = useState<Array<{ label: string; lat: number; lng: number; kind: string }> | null>(null);
  const [searching, setSearching] = useState(false);
  const touched = useRef(false);
  useEffect(() => { if (!touched.current) setQuery(placeHint ?? ''); }, [placeHint]);

  async function search() {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const found = await findPlaces(query.trim());
      setPlaces(found);
      if (found.length === 0) setPlaces([]);
    } catch {
      setPlaces([]);
    } finally {
      setSearching(false);
    }
  }

  function goTo(place: { lat: number; lng: number; kind: string }) {
    setPlaces(null);
    map.current?.setView([place.lat, place.lng], place.kind === 'Coordinates' ? 19 : 17);
  }

  // No boundary yet: open on the plot's village rather than the whole country, so the person
  // drawing starts where the plot is (found on production, 2026-09-21).
  const centredFor = useRef<string | null>(null);
  useEffect(() => {
    if (value || !placeHint || centredFor.current === placeHint || !map.current) return;
    centredFor.current = placeHint;
    findPlaces(placeHint)
      .then((found) => { if (found[0] && map.current) map.current.setView([found[0].lat, found[0].lng], 16); })
      .catch(() => undefined);
  }, [placeHint, value]);

  function switchBase(next: 'satellite' | 'map') {
    const instance = map.current;
    const layers = baseLayers.current;
    if (!instance || !layers || next === base) return;
    instance.removeLayer(layers[base]);
    // Tiles live in Leaflet's tile pane, below the boundary's overlay pane, so the drawn
    // shape stays on top whichever base is showing.
    layers[next].addTo(instance);
    setBase(next);
  }

  // Existing boundary.
  useEffect(() => {
    if (!map.current || !layer.current || drawing) return;
    layer.current.clearLayers();
    if (!value) return;
    const shape = L.geoJSON(value as unknown as GeoJSON.GeoJsonObject, {
      style: { color: BOUNDARY, weight: 3, fillColor: BOUNDARY, fillOpacity: 0.12 },
    });
    shape.addTo(layer.current);
    const bounds = shape.getBounds();
    if (bounds.isValid()) map.current.fitBounds(bounds, { padding: [24, 24] });
  }, [value, drawing]);

  // Drawing: vertices, the closing line, and draggable handles.
  useEffect(() => {
    if (!map.current || !layer.current || !drawing) return;
    layer.current.clearLayers();

    if (points.length > 0) {
      const line = points.length > 2 ? [...points, points[0]!] : points;
      L.polyline(line, { color: BOUNDARY, weight: 3, dashArray: '6,5' }).addTo(layer.current);
    }
    if (points.length > 2) {
      L.polygon(points, { color: BOUNDARY, weight: 1, fillColor: BOUNDARY, fillOpacity: 0.12 }).addTo(layer.current);
    }
    points.forEach(([lat, lng], index) => {
      const handle = L.circleMarker([lat, lng], {
        radius: 6, color: '#111827', fillColor: BOUNDARY, fillOpacity: 1, weight: 2,
      });
      handle.addTo(layer.current!);
      // Dragging a vertex: Leaflet's circleMarker is not draggable, so the map's own drag is
      // suspended while a handle is held and the point follows the pointer.
      handle.on('mousedown', () => {
        map.current!.dragging.disable();
        const move = (event: L.LeafletMouseEvent) => {
          setPoints((current) => current.map((point, i) => (i === index ? [event.latlng.lat, event.latlng.lng] : point)));
        };
        const stop = () => {
          map.current!.off('mousemove', move);
          map.current!.off('mouseup', stop);
          map.current!.dragging.enable();
        };
        map.current!.on('mousemove', move);
        map.current!.on('mouseup', stop);
      });
    });
  }, [points, drawing]);

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    const onClick = (event: L.LeafletMouseEvent) => {
      if (drawing) {
        setPoints((current) => [...current, [event.latlng.lat, event.latlng.lng]]);
        return;
      }
      if (!marking) return;
      if (marking.geometry_type === 'point') {
        // A point asset is one tap: done.
        onMarkAsset?.(marking, { type: 'Point', coordinates: [event.latlng.lng, event.latlng.lat] });
        setMarking(null);
        return;
      }
      setMarkPoints((current) => [...current, [event.latlng.lat, event.latlng.lng]]);
    };
    instance.on('click', onClick);
    return () => { instance.off('click', onClick); };
  }, [drawing, marking, onMarkAsset]);

  // Assets already marked, plus the one being marked.
  useEffect(() => {
    const target = assetLayer.current;
    if (!target) return;
    target.clearLayers();
    for (const asset of assets) {
      if (!asset.geometry) continue;
      const colour = ASSET_GROUP_COLOURS[asset.group ?? ''] ?? '#e5e7eb';
      L.geoJSON(asset.geometry as unknown as GeoJSON.GeoJsonObject, {
        style: { color: colour, weight: 3, fillColor: colour, fillOpacity: 0.25 },
        pointToLayer: (_feature, latlng) => L.circleMarker(latlng, {
          radius: 7, color: '#111827', weight: 2, fillColor: colour, fillOpacity: 1,
        }),
      }).bindTooltip(asset.label).addTo(target);
    }
    if (marking && markPoints.length > 0) {
      const colour = ASSET_GROUP_COLOURS[marking.asset_group] ?? '#e5e7eb';
      if (marking.geometry_type === 'area' && markPoints.length > 2) {
        L.polygon(markPoints, { color: colour, weight: 2, dashArray: '6,5', fillColor: colour, fillOpacity: 0.2 }).addTo(target);
      } else {
        L.polyline(markPoints, { color: colour, weight: 3, dashArray: '6,5' }).addTo(target);
      }
      markPoints.forEach((point) => L.circleMarker(point, {
        radius: 5, color: '#111827', weight: 2, fillColor: colour, fillOpacity: 1,
      }).addTo(target));
    }
  }, [assets, marking, markPoints]);

  function startMarking(typeId: string) {
    const type = assetTypes.find((entry) => entry.id === typeId) ?? null;
    setMarkPoints([]);
    setMarking(type);
  }

  function finishMarking() {
    if (!marking) return;
    const needed = marking.geometry_type === 'area' ? 3 : 2;
    if (markPoints.length < needed) return;
    const coords = markPoints.map(([lat, lng]) => [lng, lat]);
    const geometry = marking.geometry_type === 'area'
      ? { type: 'Polygon', coordinates: [[...coords, coords[0]!]] }
      : { type: 'LineString', coordinates: coords };
    onMarkAsset?.(marking, geometry);
    setMarking(null);
    setMarkPoints([]);
  }

  function startDrawing() {
    setPoints(value ? ringToPoints(value) : []);
    setDrawing(true);
  }

  async function importFile(file: File) {
    try {
      const corners = await readBoundaryFile(file);
      setPoints(corners);
      if (map.current) map.current.fitBounds(L.latLngBounds(corners.map(([lat, lng]) => L.latLng(lat, lng))).pad(0.4));
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'The file could not be read.');
    }
  }

  function finish() {
    if (points.length < 3) return;
    setDrawing(false);
    onChange?.(pointsToPolygon(points));
  }

  function cancel() {
    setDrawing(false);
    setPoints([]);
  }

  function download() {
    const geometry = drawing && points.length > 2 ? pointsToPolygon(points) : value;
    if (!geometry) return;
    // A bare geometry is valid GeoJSON but most tools expect a FeatureCollection, and the
    // plot code has to travel with the shape or the file is anonymous once downloaded.
    const sqm = geometryAreaSqm(geometry);
    const feature = {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        properties: {
          plot_code: plotCode ?? null,
          area_ha: Number((sqm / SQM_PER_HA).toFixed(4)),
          area_sqm: Math.round(sqm),
        },
        geometry,
      }],
    };
    const blob = new Blob([JSON.stringify(feature, null, 2)], { type: 'application/geo+json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    // Farmer-based plot codes contain '/', which is not allowed in a file name.
    anchor.download = `${(plotCode ?? 'plot').replace(/[\\/:*?"<>|]+/g, '-')}.geojson`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  const hasShape = Boolean(value) || points.length > 2;
  // Live while drawing, so the officer can check the shape against the declared area before
  // saving. The saved figure is recomputed by PostGIS.
  // Only while drawing: once saved, the stored PostGIS figure (shown beside the map) is the one
  // that counts, and a second, slightly different number for the same boundary would confuse.
  const liveSqm = drawing && points.length > 2 ? geometryAreaSqm(pointsToPolygon(points)) : 0;

  return (
    <div className="plot-map">
      <div className="plot-map-tools">
        <button aria-pressed={base === 'satellite'} className="map-base-toggle"
                onClick={() => switchBase(base === 'satellite' ? 'map' : 'satellite')}
                title={base === 'satellite' ? 'Show the street map' : 'Show satellite imagery'} type="button">
          {base === 'satellite' ? <><MapIcon size={15} /> Map</> : <><Satellite size={15} /> Satellite</>}
        </button>
        {/* Edit, then Save (owner, 2026-09-21). Editing opens the saved corners for dragging;
            nothing reaches the server until Save. There is no "remove": a boundary is corrected
            by editing it, and every saved version is kept as a geometry revision. */}
        {/* Only where the boundary can be saved: the Risk assessment page marks assets on a plot
            without being the place its boundary is changed. */}
        {!readOnly && onChange && !drawing && !marking && (
          <button className="primary" onClick={startDrawing} type="button">
            <Pencil size={15} /> {value ? 'Edit boundary' : 'Draw boundary'}
          </button>
        )}
        {!readOnly && drawing && (
          <>
            <button className="primary" disabled={points.length < 3} onClick={finish}
                    title={points.length < 3 ? 'Place at least three corners' : 'Save this boundary'} type="button">
              <Check size={15} /> Save boundary
            </button>
            <button disabled={points.length === 0} onClick={() => setPoints((c) => c.slice(0, -1))} type="button">
              <Undo2 size={15} /> Undo point
            </button>
            <button disabled={points.length === 0} onClick={() => setPoints([])} type="button">
              <Trash2 size={15} /> Start over
            </button>
            {/* A boundary from Google Earth / My Maps or a GIS file (owner, 2026-09-28). */}
            <label className="button-like">
              <Upload size={15} /> Import KML / KMZ / GeoJSON
              <input accept=".kml,.kmz,.geojson,.json" hidden onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; if (file) void importFile(file); }} type="file" />
            </label>
            <button className="cancel" onClick={cancel} type="button">Cancel</button>
          </>
        )}
        {!readOnly && !drawing && !marking && onMarkAsset && assetTypes.length > 0 && (
          <select aria-label="Mark an asset or risk" className="asset-type-picker" onChange={(event) => startMarking(event.target.value)} value="">
            <option value="">+ Mark asset / risk...</option>
            {[...new Set(assetTypes.map((type) => type.asset_group))].map((group) => (
              <optgroup key={group} label={group}>
                {assetTypes.filter((type) => type.asset_group === group).map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.label} ({type.geometry_type === 'point' ? 'point' : type.geometry_type === 'line' ? 'line' : 'area'})
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        )}
        {marking && (
          <>
            {marking.geometry_type !== 'point' && (
              <button className="primary" disabled={markPoints.length < (marking.geometry_type === 'area' ? 3 : 2)}
                      onClick={finishMarking} type="button">
                <Check size={15} /> Finish {marking.label.toLowerCase()}
              </button>
            )}
            {marking.geometry_type !== 'point' && (
              <button disabled={markPoints.length === 0} onClick={() => setMarkPoints((c) => c.slice(0, -1))} type="button">
                <Undo2 size={15} /> Undo point
              </button>
            )}
            <button className="cancel" onClick={() => { setMarking(null); setMarkPoints([]); }} type="button">Cancel</button>
          </>
        )}
        {!drawing && !marking && (
          <button disabled={!hasShape} onClick={download} type="button">
            <Download size={15} /> Download GeoJSON
          </button>
        )}
      </div>
      {marking && (
        <p className="plot-map-hint">
          {marking.geometry_type === 'point'
            ? `Tap the map where the ${marking.label.toLowerCase()} is.`
            : marking.geometry_type === 'line'
              ? `Tap along the ${marking.label.toLowerCase()}, point by point, then Finish.`
              : `Tap around the edge of the ${marking.label.toLowerCase()}, then Finish.`}
          {' '}It can be inside the plot or on its boundary.
        </p>
      )}
      {drawing && (
        <p className="plot-map-hint">
          {value
            ? 'Drag a yellow corner to move it, or tap the map to add a corner. Save when the shape matches the ground.'
            : 'Tap the map to place each corner of the plot, following the boundary. Drag a corner to move it. Three corners close the shape; then Save.'}
          {' '}{points.length} corner{points.length === 1 ? '' : 's'} placed.
        </p>
      )}
      {liveSqm > 0 && (
        <p className="plot-map-area">
          Area so far (approx.): <strong>{formatArea(liveSqm / SQM_PER_HA)}</strong>
        </p>
      )}
      <div className="map-search">
        <Search size={15} />
        <input onChange={(event) => { touched.current = true; setQuery(event.target.value); }}
               onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void search(); } }}
               placeholder="Search a village, landmark or address - or paste 22.97, 78.66" value={query} />
        <button disabled={searching || !query.trim()} onClick={() => void search()} type="button">{searching ? 'Searching...' : 'Search'}</button>
        {places && (
          <ul className="map-search-results">
            {places.length === 0
              ? <li className="muted" style={{ padding: '8px 10px' }}>Nothing found. Try the village and district, or paste coordinates.</li>
              : places.map((place) => (
                <li key={`${place.lat},${place.lng}`}>
                  <button onClick={() => goTo(place)} type="button">
                    <strong>{place.label}</strong>{place.kind && <small>{place.kind}</small>}
                  </button>
                </li>
              ))}
          </ul>
        )}
      </div>
      <div className="plot-map-canvas" ref={container} />
    </div>
  );
}

/**
 * GeoJSON is [lng, lat]; Leaflet is [lat, lng]. Mixing them puts Indian plots in Somalia.
 *
 * Editing works on the FIRST ring of the first part. A multi-part plot is rendered whole but
 * redrawn as one shape, which is honest: the alternative is a half-edited boundary where the
 * other parts silently survive.
 */
function ringToPoints(geometry: PlotGeometry): Array<[number, number]> {
  const ring = (geometry.type === 'MultiPolygon'
    ? geometry.coordinates[0]?.[0]
    : geometry.coordinates[0]) ?? [];
  const open = ring.length > 1 && ring[0]![0] === ring[ring.length - 1]![0] && ring[0]![1] === ring[ring.length - 1]![1]
    ? ring.slice(0, -1)
    : ring;
  return open.map((pair) => [pair[1]!, pair[0]!]);
}

function pointsToPolygon(points: Array<[number, number]>): PolygonGeoJson {
  const ring = points.map(([lat, lng]) => [lng, lat]);
  const first = ring[0]!;
  // A GeoJSON ring must be closed. PostGIS rejects one that is not.
  return { type: 'Polygon', coordinates: [[...ring, first]] };
}
